import { useEffect, useState } from 'react';
import { Plus, Pencil, Trash2, Loader2, Check, X, ArrowUp, ArrowDown, Circle } from 'lucide-react';
import { saApi } from '@/contexts/SuperAdminAuthContext';
import { toast } from '@/components/ui/toast';
import { ConfirmModal } from '@/components/ConfirmModal';

// ── Super Admin: System Lead Statuses ───────────────────────────────
//
// Platform defaults that every tenant sees in Settings > Lead Statuses
// with a Default badge. Tenants can't edit or delete these — they add
// their own "Custom" statuses alongside. Full CRUD + reorder lives here.

interface SystemLeadStatus {
  id: string;
  name: string;
  color: string;
  order: number;
  isActive: boolean;
  createdAt: string;
}

const PRESET_COLORS = [
  '#3B82F6', '#8B5CF6', '#EC4899', '#EF4444', '#F59E0B',
  '#22C55E', '#10B981', '#06B6D4', '#64748B', '#6B7280',
];

// Inline section — used as a tab inside SuperAdminSettingsPage.
export function LeadStatusesSection() {
  const [statuses, setStatuses] = useState<SystemLeadStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [addName, setAddName] = useState('');
  const [addColor, setAddColor] = useState(PRESET_COLORS[0]);
  const [adding, setAdding] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editColor, setEditColor] = useState('');
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<SystemLeadStatus | null>(null);

  const load = () => {
    setLoading(true);
    saApi.get('/super-admin/lead-statuses')
      .then(({ data }) => setStatuses(Array.isArray(data) ? data : []))
      .catch(() => setStatuses([]))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const handleAdd = async () => {
    if (!addName.trim()) return;
    setAdding(true);
    try {
      await saApi.post('/super-admin/lead-statuses', { name: addName.trim(), color: addColor });
      setAddName('');
      setAddColor(PRESET_COLORS[0]);
      setShowAdd(false);
      load();
      toast('success', 'Status added');
    } catch (err: any) {
      toast('error', err?.response?.data?.message || 'Failed to add');
    } finally {
      setAdding(false);
    }
  };

  const startEdit = (s: SystemLeadStatus) => {
    setEditId(s.id);
    setEditName(s.name);
    setEditColor(s.color);
  };

  const handleSaveEdit = async (id: string) => {
    if (!editName.trim()) return;
    setSaving(true);
    try {
      await saApi.patch(`/super-admin/lead-statuses/${id}`, { name: editName.trim(), color: editColor });
      setEditId(null);
      load();
      toast('success', 'Status updated');
    } catch (err: any) {
      toast('error', err?.response?.data?.message || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (s: SystemLeadStatus) => {
    try {
      await saApi.patch(`/super-admin/lead-statuses/${s.id}`, { isActive: !s.isActive });
      load();
    } catch (err: any) {
      toast('error', err?.response?.data?.message || 'Failed to toggle');
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      await saApi.delete(`/super-admin/lead-statuses/${confirmDelete.id}`);
      toast('success', 'Status deleted');
      setConfirmDelete(null);
      load();
    } catch (err: any) {
      toast('error', err?.response?.data?.message || 'Failed to delete');
    }
  };

  const handleMove = async (index: number, direction: 'up' | 'down') => {
    const next = [...statuses];
    const swap = direction === 'up' ? index - 1 : index + 1;
    if (swap < 0 || swap >= next.length) return;
    [next[index], next[swap]] = [next[swap], next[index]];
    setStatuses(next); // optimistic
    try {
      await saApi.patch('/super-admin/lead-statuses/reorder', { ids: next.map((s) => s.id) });
    } catch (err: any) {
      toast('error', 'Failed to reorder');
      load();
    }
  };

  return (
    <>
      <div className="space-y-5">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h2 className="text-lg font-bold text-[#1E293B]">Lead Statuses</h2>
            <p className="text-xs text-[#64748B] mt-0.5">
              Platform-wide defaults. Every tenant sees these as read-only options.
            </p>
          </div>
          <button
            onClick={() => setShowAdd(true)}
            className="flex items-center gap-2 px-4 py-2 bg-[#6366F1] hover:bg-[#4F46E5] text-white text-sm font-semibold rounded-lg"
          >
            <Plus className="h-4 w-4" /> New Status
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="h-6 w-6 animate-spin text-[#6366F1]" />
          </div>
        ) : (
          <div className="bg-white border border-gray-100 rounded-xl overflow-hidden">
            {showAdd && (
              <div className="flex items-center gap-3 border-b border-gray-100 bg-slate-50/50 px-4 py-3">
                <input
                  autoFocus
                  className="flex-1 h-9 px-3 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#6366F1]"
                  placeholder="Status name — e.g. Qualified"
                  value={addName}
                  onChange={(e) => setAddName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
                />
                <div className="flex gap-1">
                  {PRESET_COLORS.map((c) => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setAddColor(c)}
                      className={`h-6 w-6 rounded-full border-2 ${addColor === c ? 'border-slate-800 scale-110' : 'border-transparent'}`}
                      style={{ backgroundColor: c }}
                    />
                  ))}
                </div>
                <button
                  onClick={handleAdd}
                  disabled={adding || !addName.trim()}
                  className="h-9 px-3 rounded-lg bg-[#6366F1] text-white hover:bg-[#4F46E5] disabled:opacity-50"
                >
                  {adding ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                </button>
                <button
                  onClick={() => { setShowAdd(false); setAddName(''); }}
                  className="text-slate-400 hover:text-slate-700"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            )}

            {statuses.length === 0 ? (
              <div className="text-center py-16">
                <Circle className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                <p className="text-sm text-slate-500 mb-3">No system statuses yet</p>
                <button
                  onClick={() => setShowAdd(true)}
                  className="px-4 py-2 bg-[#6366F1] hover:bg-[#4F46E5] text-white text-sm font-semibold rounded-lg"
                >
                  Add Your First Status
                </button>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {statuses.map((s, idx) => {
                  const editing = editId === s.id;
                  return (
                    <div key={s.id} className="group flex items-center gap-3 px-4 py-3 hover:bg-slate-50/60">
                      {editing ? (
                        <>
                          <div className="flex gap-1 flex-shrink-0">
                            {PRESET_COLORS.map((c) => (
                              <button
                                key={c}
                                type="button"
                                onClick={() => setEditColor(c)}
                                className={`h-5 w-5 rounded-full border-2 ${editColor === c ? 'border-slate-800 scale-110' : 'border-transparent'}`}
                                style={{ backgroundColor: c }}
                              />
                            ))}
                          </div>
                          <input
                            autoFocus
                            className="flex-1 h-8 px-2.5 rounded border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#6366F1]"
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleSaveEdit(s.id);
                              if (e.key === 'Escape') setEditId(null);
                            }}
                          />
                          <button
                            onClick={() => handleSaveEdit(s.id)}
                            className="text-emerald-600 hover:text-emerald-700"
                            disabled={saving}
                          >
                            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                          </button>
                          <button onClick={() => setEditId(null)} className="text-slate-400 hover:text-slate-700">
                            <X className="h-4 w-4" />
                          </button>
                        </>
                      ) : (
                        <>
                          <span
                            className="h-3.5 w-3.5 rounded-full flex-shrink-0"
                            style={{ backgroundColor: s.color }}
                          />
                          <span className={`flex-1 text-sm font-medium ${s.isActive ? 'text-[#1E293B]' : 'text-slate-400 line-through'}`}>
                            {s.name}
                          </span>
                          <button
                            onClick={() => handleMove(idx, 'up')}
                            disabled={idx === 0}
                            className="text-slate-400 hover:text-[#6366F1] disabled:opacity-30 opacity-0 group-hover:opacity-100"
                            title="Move up"
                          >
                            <ArrowUp className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleMove(idx, 'down')}
                            disabled={idx === statuses.length - 1}
                            className="text-slate-400 hover:text-[#6366F1] disabled:opacity-30 opacity-0 group-hover:opacity-100"
                            title="Move down"
                          >
                            <ArrowDown className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleToggle(s)}
                            className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${s.isActive ? 'bg-[#6366F1]' : 'bg-slate-200'}`}
                            title={s.isActive ? 'Active — visible to tenants' : 'Hidden from tenants'}
                          >
                            <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${s.isActive ? 'translate-x-5' : 'translate-x-0.5'}`} />
                          </button>
                          <button
                            onClick={() => startEdit(s)}
                            className="text-slate-400 hover:text-[#6366F1]"
                            title="Edit"
                          >
                            <Pencil className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => setConfirmDelete(s)}
                            className="text-slate-400 hover:text-red-500"
                            title="Delete"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      <ConfirmModal
        isOpen={!!confirmDelete}
        title="Delete system status?"
        message="This status will disappear from every tenant's dropdown. Tenants who created a custom status with the same name will keep theirs."
        confirmText="Delete"
        onConfirm={handleDelete}
        onCancel={() => setConfirmDelete(null)}
      />
    </>
  );
}
